import React, { useMemo } from 'react';
import { modalStyles as styles } from './modalStyles';

const ACCESSORY_LABELS = {
  ac_power_cable: 'AC power cable',
  hdmi_cable: 'HDMI cable',
  dp_cable: 'DP cable'
};

const canonicalAccessoryCode = value => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  return trimmed
    .replace(/qty$/i, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/__+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
};

const formatAccessoryLabel = code => {
  const canonical = canonicalAccessoryCode(code);
  if (canonical && ACCESSORY_LABELS[canonical]) {
    return ACCESSORY_LABELS[canonical];
  }
  const source = typeof code === 'string' && code.trim() ? code.trim() : canonical;
  if (!source) return 'Accessory';
  return source.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
};

const formatAccessoryList = items => {
  if (!Array.isArray(items) || items.length === 0) {
    return 'None recorded';
  }
  const counts = items.reduce((acc, entry) => {
    if (!entry) return acc;
    if (typeof entry === 'string') {
      const canonical = canonicalAccessoryCode(entry) || entry;
      acc[canonical] = (acc[canonical] || 0) + 1;
      return acc;
    }
    const canonical = canonicalAccessoryCode(entry.code || entry.token || '');
    if (!canonical) return acc;
    const qty = entry.included === false ? 0 : Math.max(0, Number(entry.qty || entry.quantity) || 0);
    if (qty === 0) return acc;
    acc[canonical] = (acc[canonical] || 0) + qty;
    return acc;
  }, {});
  const entries = Object.entries(counts);
  if (!entries.length) return 'None recorded';
  return entries
    .map(([canonical, qty]) => {
      const label = formatAccessoryLabel(canonical);
      return qty > 1 ? `${label} (x${qty})` : label;
    })
    .join(', ');
};

const formatMountingOption = value => {
  if (!value) return 'Not recorded';
  return value.replace(/_/g, ' ');
};

const formatHmiIncluded = value => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return 'Not recorded';
};

const normalizeStatusLabel = status => {
  switch (status) {
    case 'awaiting_repair':
      return 'Awaiting Repair';
    case 'repairing':
      return 'Repairing';
    case 'awaiting_shipping':
      return 'Awaiting Shipping';
    case 'shipped':
      return 'Shipped';
    default:
      return status ? status.replace(/_/g, ' ') : 'Unknown';
  }
};

const buildMismatchDetails = fields => {
  if (!Array.isArray(fields) || fields.length === 0) {
    return [];
  }
  const details = [];
  fields.forEach(field => {
    if (field === 'mainDeviceSerial') {
      details.push('Main device serial number differs.');
    } else if (field === 'mountingOption') {
      details.push('Mounting option differs.');
    } else if (field === 'hmiIncluded') {
      details.push('HMI inclusion differs.');
    } else if (field.startsWith('accessories:')) {
      const token = field.split(':')[1];
      const label = ACCESSORY_LABELS[token] || formatAccessoryLabel(token);
      details.push(`Accessory quantity differs: ${label}.`);
    }
  });
  return details;
};

export default function CustomerRmaModalReceived({ rma, onClose }) {
  const receipt = rma?.receipt || null;
  const pending = !receipt || !receipt.confirmedAt;

  const mismatchDetails = useMemo(() => buildMismatchDetails(receipt?.mismatch?.fields), [receipt?.mismatch?.fields]);

  const confirmationTimestamp = receipt?.confirmedAt
    ? new Date(receipt.confirmedAt).toLocaleString()
    : null;

  const currentStatusLabel = normalizeStatusLabel(rma?.status);

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2>Device Received</h2>
        {pending ? (
          <>
            <p>Package received. Intake checks pending.</p>
            <p>
              <strong>Device:</strong> {rma?.serialNumber || 'Unknown'} → {rma?.productNumber || 'Not available'}
            </p>
          </>
        ) : (
          <>
            <p>
              Receipt checks complete.{' '}
              {receipt?.mismatch?.hasMismatch
                ? 'A mismatch was detected.'
                : 'Matches expected configuration.'}
            </p>
            {receipt?.confirmedBy && (
              <p>
                <strong>Confirmed by:</strong> {receipt.confirmedBy}
                {confirmationTimestamp ? ` on ${confirmationTimestamp}` : ''}
              </p>
            )}
            <div style={infoSection}>
              <h3 style={sectionHeading}>Expected vs. found</h3>
              <div style={infoRow}>
                <span style={infoLabel}>Expected serial</span>
                <span>{receipt?.expected?.mainDeviceSerial || 'Not recorded'}</span>
              </div>
              <div style={infoRow}>
                <span style={infoLabel}>Found serial</span>
                <span>{receipt?.found?.mainDeviceSerial || 'Not recorded'}</span>
              </div>
              <div style={infoRow}>
                <span style={infoLabel}>Expected mounting</span>
                <span>{formatMountingOption(receipt?.expected?.mountingOption)}</span>
              </div>
              <div style={infoRow}>
                <span style={infoLabel}>Found mounting</span>
                <span>{formatMountingOption(receipt?.found?.mountingOption)}</span>
              </div>
              <div style={infoRow}>
                <span style={infoLabel}>Expected HMI</span>
                <span>{formatHmiIncluded(receipt?.expected?.hmiIncluded)}</span>
              </div>
              <div style={infoRow}>
                <span style={infoLabel}>Found HMI</span>
                <span>{formatHmiIncluded(receipt?.found?.hmiIncluded)}</span>
              </div>
              <div style={infoRow}>
                <span style={infoLabel}>Expected accessories</span>
                <span>{formatAccessoryList(receipt?.expected?.accessories || [])}</span>
              </div>
              <div style={infoRow}>
                <span style={infoLabel}>Found accessories</span>
                <span>{formatAccessoryList(receipt?.found?.accessories || [])}</span>
              </div>
            </div>
            {mismatchDetails.length > 0 && (
              <div style={infoSection}>
                <h3 style={sectionHeading}>Mismatch details</h3>
                <ul style={detailList}>
                  {mismatchDetails.map(detail => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              </div>
            )}
            {receipt?.mismatch?.summary && (
              <div style={infoSection}>
                <h3 style={sectionHeading}>Intake summary</h3>
                <p style={{ margin: 0 }}>{receipt.mismatch.summary}</p>
              </div>
            )}
            <div style={infoSection}>
              <h3 style={sectionHeading}>Current status</h3>
              <p>
                <strong>{currentStatusLabel}</strong>
              </p>
            </div>
          </>
        )}

        <button onClick={onClose} style={styles.button}>Close</button>
      </div>
    </div>
  );
}

const infoSection = {
  textAlign: 'left',
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: '6px',
  padding: '0.75rem',
  margin: '0.75rem 0',
  backgroundColor: 'rgba(255,255,255,0.05)'
};

const sectionHeading = {
  margin: '0 0 0.5rem',
  fontSize: '1rem'
};

const infoRow = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '1rem',
  marginBottom: '0.35rem'
};

const infoLabel = {
  fontWeight: 600
};

const detailList = {
  margin: 0,
  paddingLeft: '1.1rem'
};
